import { useEffect, useRef, useState } from "react";
import { prepareZXingModule, readBarcodes } from "zxing-wasm/reader";
import { Camera, ScanBarcode, X } from "lucide-react";
import { toast } from "@/components/Toast";
import { parseDniPdf417 } from "@/lib/dniBarcode";

// Motor zxing-wasm (ZXing-C++ en WebAssembly, gratuito). El .wasm se sirve desde /public (sin CDN externo).
prepareZXingModule({
  overrides: {
    locateFile: (path, prefix) => (path.endsWith(".wasm") ? `${process.env.PUBLIC_URL || ""}/${path}` : prefix + path),
  },
});

const READER_OPTS = { formats: ["PDF417"], tryHarder: true, tryRotate: true, tryInvert: true, tryDownscale: true, maxNumberOfSymbols: 1 };

const decodeInput = async (input) => {
  const results = await readBarcodes(input, READER_OPTS);
  const hit = results.find((r) => r.isValid && r.text);
  return hit ? hit.text : null;
};

const cameraErrorMessage = (e) => {
  const det = String((e && (e.name || e.message)) || e || "");
  if (/NotAllowed|Permission/i.test(det)) return "El navegador no tiene permiso para usar la cámara. Habilitalo en los ajustes del celular y probá de nuevo.";
  if (/NotReadable|Could not start|TrackStart/i.test(det)) return "La cámara está siendo usada por otra app. Cerrala y probá de nuevo.";
  if (/NotFound|Overconstrained|DevicesNotFound/i.test(det)) return "No se encontró una cámara trasera disponible en este celular.";
  if (/not supported|insecure|undefined/i.test(det)) return "Este navegador no permite usar la cámara. Usá \"Sacar foto\" o abrí la plataforma en Chrome o Safari actualizado.";
  return `No pudimos abrir la cámara (${det.slice(0, 80) || "error desconocido"}). Usá "Sacar foto" o completá a mano.`;
};

const openStream = async () => {
  const hi = { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } };
  try {
    return await navigator.mediaDevices.getUserMedia({ video: { ...hi, advanced: [{ focusMode: "continuous" }] }, audio: false });
  } catch (e) {
    if (/NotAllowed|Permission/i.test(String(e && (e.name || e.message)))) throw e;
    return navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
  }
};

/** Lee el PDF417 del DNI argentino. Escaneo en vivo con cámara y alternativa "Sacar foto"
 * (foto en máxima resolución, suele leer mejor DNIs gastados). Nunca traba: si falla, se completa a mano. */
export function DniBarcodeScanner({ onDecoded, testIdPrefix = "dnibar" }) {
  const [open, setOpen] = useState(false);
  const [busyPhoto, setBusyPhoto] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const loopRef = useRef(null);
  const activeRef = useRef(false);
  const fileRef = useRef(null);
  // Fix BJ (2026-10-23): cada stop() invalida el start() en curso. Antes, si se cerraba la ventana
  // mientras el celular pedía permiso, al aceptar la cámara quedaba prendida y el bucle esperando el video.
  const sessionRef = useRef(0);

  const stop = () => {
    sessionRef.current += 1;
    activeRef.current = false;
    clearTimeout(loopRef.current);
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setOpen(false);
  };

  useEffect(() => () => stop(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDecoded = (raw) => {
    stop();
    const parsed = parseDniPdf417(raw);
    if (!parsed) {
      toast.error("Se leyó un código, pero no es el del DNI. Apuntá al código de barras del DNI o completá los datos a mano.");
      return;
    }
    onDecoded(parsed);
  };

  const scanFrame = async (canvas) => {
    const v = videoRef.current;
    if (!activeRef.current || !v) return;
    if (v.readyState >= 2 && v.videoWidth) {
      // Recorte central amplio: el PDF417 del DNI es largo y denso.
      const sw = Math.floor(v.videoWidth * 0.95);
      const sh = Math.floor(Math.min(v.videoHeight * 0.8, v.videoWidth * 0.6));
      const sx = Math.floor((v.videoWidth - sw) / 2);
      const sy = Math.floor((v.videoHeight - sh) / 2);
      canvas.width = sw; canvas.height = sh;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(v, sx, sy, sw, sh, 0, 0, sw, sh);
      try {
        const text = await decodeInput(ctx.getImageData(0, 0, sw, sh));
        if (text && activeRef.current) { handleDecoded(text); return; }
      } catch { /* cuadro ilegible, se sigue */ }
    }
    if (activeRef.current) loopRef.current = setTimeout(() => scanFrame(canvas), 120);
  };

  const start = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) { toast.error(cameraErrorMessage("not supported")); return; }
    const session = ++sessionRef.current; // Fix BJ
    setOpen(true);
    try {
      const stream = await openStream();
      if (session !== sessionRef.current) { stream.getTracks().forEach((t) => t.stop()); return; } // se cerró mientras pedía permiso
      streamRef.current = stream;
      activeRef.current = true;
      while (!videoRef.current) {
        if (session !== sessionRef.current) return; // stop() ya apagó la cámara
        await new Promise((r) => setTimeout(r, 30));
      }
      const v = videoRef.current;
      v.srcObject = stream;
      await v.play();
      scanFrame(document.createElement("canvas"));
    } catch (e) {
      if (session !== sessionRef.current) return; // cerrada a propósito: sin aviso de error
      toast.error(cameraErrorMessage(e));
      stop();
    }
  };

  const onPhoto = async (ev) => {
    const file = ev.target.files && ev.target.files[0];
    ev.target.value = "";
    if (!file) return;
    stop();
    setBusyPhoto(true);
    try {
      const text = await decodeInput(file);
      if (text) handleDecoded(text);
      else toast.error("No se encontró el código en la foto. Sacala de cerca, derecha, con buena luz y sin reflejos, o completá a mano.");
    } catch {
      toast.error("No pudimos procesar la foto. Probá de nuevo o completá los datos a mano.");
    } finally {
      setBusyPhoto(false);
    }
  };

  const btn = { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", border: "1.5px solid #0A0A0A",
    background: "#fff", color: "#0A0A0A", fontWeight: 800, fontSize: 11, borderRadius: 6, cursor: "pointer",
    textTransform: "uppercase", letterSpacing: "0.04em" };

  return (
    <>
      <span style={{ display: "inline-flex", gap: 8, flexWrap: "wrap" }}>
        <button type="button" onClick={start} data-testid={`${testIdPrefix}-scan-btn`} style={btn}>
          <ScanBarcode size={13} /> Escanear DNI (código de barras)
        </button>
        <button type="button" onClick={() => fileRef.current && fileRef.current.click()} disabled={busyPhoto}
          data-testid={`${testIdPrefix}-photo-btn`} style={{ ...btn, opacity: busyPhoto ? 0.6 : 1 }}>
          <Camera size={13} /> {busyPhoto ? "Leyendo foto..." : "Sacar foto del DNI"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPhoto}
          style={{ display: "none" }} data-testid={`${testIdPrefix}-photo-input`} />
      </span>
      {open && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 300, display: "flex",
          flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 16 }}
          data-testid={`${testIdPrefix}-scan-modal`}>
          <div style={{ background: "#111", borderRadius: 8, padding: 12, width: "100%", maxWidth: 420 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ color: "#FFFBF4", fontSize: 12, fontWeight: 800 }}>Apuntá al código de barras del DNI</span>
              <button type="button" onClick={stop} data-testid={`${testIdPrefix}-scan-close`}
                style={{ background: "none", border: "none", color: "#FFFBF4", cursor: "pointer", padding: 4 }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ position: "relative", width: "100%", background: "#000", borderRadius: 4, overflow: "hidden" }}>
              <video ref={videoRef} playsInline muted autoPlay data-testid={`${testIdPrefix}-scan-video`}
                style={{ width: "100%", display: "block" }} />
              <div style={{ position: "absolute", left: "4%", right: "4%", top: "22%", bottom: "22%",
                border: "2px solid #22C55E", borderRadius: 4, pointerEvents: "none" }} />
            </div>
            <div style={{ color: "rgba(255,251,244,0.6)", fontSize: 10, marginTop: 8 }}>
              Acercá el DNI hasta que el código ocupe el recuadro verde, sin reflejos. Si no lee, cerrá y usá "Sacar foto del DNI".
            </div>
          </div>
        </div>
      )}
    </>
  );
}
